import { useRef } from 'react'

import { createStyles } from 'antd-style'

import { pick } from '@eastcoal/common/utils/object'

import { useImageCardStore } from '../Store/hooks/useStore'

import { ImageResizerProps } from './interface'

const IMAGE_RESIZER_SIZE = 14

const useStyles = createStyles(({ token }) => ({
  imageResizerBox: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1,
    pointerEvents: 'none',
    outline: `1px solid ${token.blue4}`,
    [`&.is-resizing::after`]: {
      content: 'attr(data-size)',
      position: 'absolute',
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: token.colorWhite,
      fontSize: token.fontSizeHeading3,
      backgroundColor: token.colorBgMask
    }
  },
  imageResizer: {
    position: 'absolute',
    zIndex: 1,
    width: IMAGE_RESIZER_SIZE,
    height: IMAGE_RESIZER_SIZE,
    borderRadius: 10,
    border: '2px solid #fff',
    backgroundColor: token.blue5,
    pointerEvents: 'all'
  },
  imageResizerTl: {
    top: -IMAGE_RESIZER_SIZE / 2,
    left: -IMAGE_RESIZER_SIZE / 2,
    cursor: 'nwse-resize'
  },
  imageResizerTr: {
    top: -IMAGE_RESIZER_SIZE / 2,
    right: -IMAGE_RESIZER_SIZE / 2,
    cursor: 'nesw-resize'
  },
  imageResizerBl: {
    bottom: -IMAGE_RESIZER_SIZE / 2,
    left: -IMAGE_RESIZER_SIZE / 2,
    cursor: 'nesw-resize'
  },
  imageResizerBr: {
    bottom: -IMAGE_RESIZER_SIZE / 2,
    right: -IMAGE_RESIZER_SIZE / 2,
    cursor: 'nwse-resize'
  }
}))

enum Direction {
  top = 0,
  right = 1,
  bottom = 2,
  left = 3
}

const PositionInitialValue = {
  startX: 0,
  startY: 0,
  directions: [Direction.top, Direction.left] as [Direction, Direction],
  isResizing: false
}

export const ImageResizer = ({ className, style }: ImageResizerProps) => {
  const { ratio, setCurrentSize, isReverseSize } = useImageCardStore(state =>
    pick(state, ['ratio', 'setCurrentSize', 'isReverseSize'])
  )

  const imageResizerBoxRef = useRef<HTMLDivElement>(null)
  const positioningRef =
    useRef<typeof PositionInitialValue>(PositionInitialValue)

  const handlePointerDown = (
    event: React.PointerEvent,
    directions: [Direction, Direction]
  ) => {
    event.preventDefault()
    const positioning = positioningRef.current
    positioning.startX = event.clientX
    positioning.startY = event.clientY
    positioning.isResizing = true
    positioning.directions = directions

    imageResizerBoxRef.current?.classList.add('is-resizing')

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
  }

  const handlePointerMove = (event: PointerEvent) => {
    const imageResizerBox = imageResizerBoxRef.current

    if (!imageResizerBox) {
      return
    }

    const { directions, isResizing, startX, startY } = positioningRef.current

    if (!isResizing) {
      return
    }

    const { clientX, clientY } = event
    const { width, height } = imageResizerBox.getBoundingClientRect()
    // 经过旋转之后是否需要交换宽高
    const reverse = isReverseSize()
    // 缩放比例
    const sizeRatio = reverse ? 1 / ratio : ratio
    // 是否等比缩放
    const restrictive = !!sizeRatio

    // x 轴的移动方向
    const xMoveDirection =
      startX - clientX >= 0 ? Direction.left : Direction.right

    // 如果是放大，终点的 x 不能比原先的小
    const endX =
      xMoveDirection === Direction.left
        ? Math.min(clientX, startX)
        : Math.max(clientX, startX)
    const gapY = Math.abs((endX - startX) / sizeRatio)
    const endY = restrictive
      ? // 水平方向都跟起点相反，说明往中心移动为缩小操作
        (directions[1] !== xMoveDirection && directions[0] === Direction.top) ||
        (directions[1] === xMoveDirection && directions[0] === Direction.bottom)
        ? startY + gapY
        : startY - gapY
      : clientY

    const x = directions[1] === Direction.left ? endX - startX : startX - endX
    const y = directions[0] === Direction.top ? endY - startY : startY - endY

    const inset = ['0', '0', '0', '0']
    inset[directions[0]] = `${y}px`
    inset[directions[1]] = `${x}px`

    imageResizerBox.style.inset = inset.join(' ')
    imageResizerBox.dataset.size = `${Math.floor(width)} x ${Math.floor(
      height
    )}`
  }

  const handlePointerUp = () => {
    const imageResizerBox = imageResizerBoxRef.current

    if (!imageResizerBox) {
      return
    }

    const { width, height } = imageResizerBox.getBoundingClientRect()
    imageResizerBox.style.inset = ''
    imageResizerBox.classList.remove('is-resizing')

    positioningRef.current = PositionInitialValue

    const reverse = isReverseSize()

    setCurrentSize({
      width: Math.floor(reverse ? height : width),
      height: Math.floor(reverse ? width : height)
    })

    document.removeEventListener('pointermove', handlePointerMove)
    document.removeEventListener('pointerup', handlePointerUp)
  }

  const { cx, styles } = useStyles()

  return (
    <div
      ref={imageResizerBoxRef}
      className={cx(styles.imageResizerBox, className)}
      style={style}
    >
      <div
        className={cx(styles.imageResizer, styles.imageResizerTl)}
        onPointerDown={event => {
          handlePointerDown(event, [Direction.top, Direction.left])
        }}
      />
      <div
        className={cx(styles.imageResizer, styles.imageResizerTr)}
        onPointerDown={event => {
          handlePointerDown(event, [Direction.top, Direction.right])
        }}
      />
      <div
        className={cx(styles.imageResizer, styles.imageResizerBl)}
        onPointerDown={event => {
          handlePointerDown(event, [Direction.bottom, Direction.left])
        }}
      />
      <div
        className={cx(styles.imageResizer, styles.imageResizerBr)}
        onPointerDown={event => {
          handlePointerDown(event, [Direction.bottom, Direction.right])
        }}
      />
    </div>
  )
}
